import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, useAuth, useConfirm, useToast } from "@ozpanel/ui";
import { Field, NumberField, Toggle } from "./Form";
import { StatusPill, type StatusTone } from "./PageHeader";

// AppHealth — whether the APP answers, as each node finds its own copy (appserver/health.go).
//
// Running is not working: an app that deadlocked or answers 500 to everything still runs. A health check
// asks the app itself — an HTTP path that must answer 2xx/3xx, or a TCP connect — and every node that runs
// the app checks its own copy. One component for the tenant's console and the operator's; the server
// decides who may change it.

type Check = {
  path: string;
  port: number;
  interval_s: number;
  timeout_s: number;
  failures: number;
  grace_s: number;
  restart: boolean;
};
type NodeHealth = { node: string; status: string; detail: string; failures: number; since: string; checked_at: string; stale?: boolean };
type HealthState = { app: string; check: Check | null; nodes?: NodeHealth[] | null; note?: string };

const defaults: Check = { path: "", port: 0, interval_s: 30, timeout_s: 5, failures: 3, grace_s: 300, restart: false };

const tone: Record<string, StatusTone> = { healthy: "ok", unhealthy: "bad", starting: "warn", outdated: "warn", not_running: "neutral" };
const label: Record<string, string> = { healthy: "healthy", unhealthy: "unhealthy", starting: "starting", outdated: "running an older version", not_running: "not running here" };

export function AppHealth({ appName }: { appName: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const name = encodeURIComponent(appName);
  const q = useQuery({
    queryKey: ["app-health", appName],
    queryFn: () => client.apiFetch<HealthState>(`/apps/${name}/health`),
    refetchInterval: 15000,
  });
  const [form, setForm] = React.useState<Check | null>(null);
  const [busy, setBusy] = React.useState(false);
  const confirm = useConfirm();

  const fail = (title: string) => (e: unknown) =>
    notify({ kind: "error", title, body: e instanceof Error ? e.message : String(e) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["app-health", appName] });

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      await client.apiFetch(`/apps/${name}/health`, { method: "PUT", body: JSON.stringify(form) });
      notify({ kind: "success", title: "Health check saved — every node checks its own copy" });
      setForm(null);
      refresh();
    } catch (e) {
      fail("Could not save the health check")(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await client.apiFetch(`/apps/${name}/health`, { method: "DELETE" });
      notify({ kind: "success", title: "Health check removed" });
      setForm(null);
      refresh();
    } catch (e) {
      fail("Could not remove the health check")(e);
    } finally {
      setBusy(false);
    }
  };

  const d = q.data;
  if (!d) return null;
  const nodes = d.nodes ?? [];
  const num = (v: string) => (v.trim() === "" ? 0 : Number(v));

  return (
    <div className="rounded border border-line p-3 text-[12.5px]">
      {confirm.dialog}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">Health check</span>
        {d.check ? (
          <span className="text-muted">
            {d.check.path ? <>GET <span className="font-mono">{d.check.path}</span> must answer 2xx/3xx</> : "the port accepts a connection"}
            {" · every "}{d.check.interval_s}s · unhealthy after {d.check.failures} failures · {d.check.grace_s}s to start
            {d.check.restart ? " · restarts when unhealthy" : ""}
          </span>
        ) : (
          <span className="text-muted">none — the app counts as up while it runs</span>
        )}
        <span className="ml-auto flex gap-2">
          {!form && (
            <Button size="sm" variant="ghost" onClick={() => setForm({ ...defaults, ...(d.check ?? {}) })}>
              {d.check ? "Edit" : "Add a health check"}
            </Button>
          )}
          {d.check && !form && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => confirm.ask({ title: "Remove the health check?", body: "The app then counts as up whenever it runs, whether or not it answers.", confirmLabel: "Remove" }, remove)}>Remove</Button>
          )}
        </span>
      </div>

      {nodes.length > 0 && (
        <table className="mt-2 w-full text-[12px]">
          <tbody>
            {nodes.map((n) => (
              <tr key={n.node} className="border-t border-line align-top">
                <td className="py-1 pr-3 font-mono">{n.node}</td>
                <td className="py-1 pr-3">
                  <StatusPill tone={n.stale ? "neutral" : tone[n.status] ?? "neutral"}>
                    {n.stale ? "not reporting" : label[n.status] ?? n.status}
                  </StatusPill>
                </td>
                <td className="py-1 text-muted">
                  {n.detail}
                  {n.status === "unhealthy" && n.failures > 0 ? ` (${n.failures} failures in a row)` : ""}
                  <span className="block text-[11px]">since {new Date(n.since).toLocaleString()} · checked {new Date(n.checked_at).toLocaleTimeString()}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Field label="Path" mono value={form.path} onChange={(v) => setForm({ ...form, path: v })} placeholder="/healthz"
            help="Must answer 2xx or 3xx. Empty: only check that the port accepts a connection." />
          <NumberField label="Port" value={form.port || ""} onChange={(v) => setForm({ ...form, port: num(v) })} placeholder="the app's own"
            help="Empty: the app's own port." />
          <NumberField label="Every" unit="s" value={form.interval_s} onChange={(v) => setForm({ ...form, interval_s: num(v) })} help="10–3600 seconds." />
          <NumberField label="Timeout" unit="s" value={form.timeout_s} onChange={(v) => setForm({ ...form, timeout_s: num(v) })} help="Shorter than the interval." />
          <NumberField label="Unhealthy after" unit="failures" value={form.failures} onChange={(v) => setForm({ ...form, failures: num(v) })} help="In a row, 1–20." />
          <NumberField label="Time to start" unit="s" value={form.grace_s} onChange={(v) => setForm({ ...form, grace_s: num(v) })}
            help="After the app starts, failures read as starting — give an app that migrates at start long enough." />
          <div className="sm:col-span-2">
            <Toggle checked={form.restart} onChange={(v) => setForm({ ...form, restart: v })}
              label="Restart this node's copy when it turns unhealthy"
              help="At most once every 10 minutes, never while it is starting."
              consequence={form.restart ? "A restart in the middle of a long operation (a migration, an import) interrupts it." : undefined} />
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <Button size="sm" disabled={busy} onClick={() => void save()}>Save</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
